import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Grid,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  List as ListIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Mail as MailIcon,
  VpnKey as KeyIcon,
  Archive as ArchiveIcon,
  CloudUpload as UploadIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircleOutline as CheckIcon,
  WarningAmber as WarningIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';

import AdminWrapper from '../components/layout/AdminWrapper';
import { usePlayerData } from '../components/data/DataContext';
import EventCalendar, { CalendarEvent } from '../components/calendar/EventCalendar';
import { gridSpacing } from '../assets/theme/muitheme';
import { DS, Meter, Overline, Panel, PageHeader, enterSx, numericSx } from '../components/shared/Dashboard';

type EventStatus = 'pending' | 'private' | 'invite' | 'public' | 'archived';

interface DashboardEvent {
  eventID: string;
  eventName: string;
  status: EventStatus;
  start?: moment.Moment;
  end?: moment.Moment;
}

interface AttentionItem {
  event: DashboardEvent;
  message: string;
  severity: 'urgent' | 'info';
}

// Tab order in EventsList: Pending, Private, Invite, Public, Archived
const STATUS_META: Record<EventStatus, { label: string; description: string; color: string; icon: React.ReactNode; tab: number }> = {
  pending: { label: 'Pending', description: 'Awaiting approval', color: DS.warning, icon: <ScheduleIcon fontSize="small" />, tab: 0 },
  private: { label: 'Private', description: 'Live, join by code', color: DS.info, icon: <LockIcon fontSize="small" />, tab: 1 },
  invite: { label: 'Invite', description: 'Live, invited players', color: DS.violet, icon: <MailIcon fontSize="small" />, tab: 2 },
  public: { label: 'Public', description: 'Live for players', color: DS.gain, icon: <PublicIcon fontSize="small" />, tab: 3 },
  archived: { label: 'Archived', description: 'Completed', color: DS.neutral, icon: <ArchiveIcon fontSize="small" />, tab: 4 },
};

// Private and invite events are live too, just restricted
const isLiveStatus = (status: EventStatus) => status === 'public' || status === 'private' || status === 'invite';

const SOON_DAYS = 7;
const MAX_LIST_ITEMS = 6;

const parseTime = (value?: string) => {
  if (!value) return undefined;
  // Event times are stored and displayed as UTC (see formatEventDate)
  const parsed = moment.utc(value);
  return parsed.isValid() ? parsed : undefined;
};

const toDashboardEvents = (events: Record<string, any>, status: EventStatus): DashboardEvent[] =>
  Object.entries(events || {}).map(([key, event]) => ({
    eventID: event?.eventID || key,
    eventName: event?.eventName || event?.eventID || key,
    status,
    start: parseTime(event?.eventStartTime),
    end: parseTime(event?.eventEndTime),
  }));

const getAttentionItems = (events: DashboardEvent[], now: moment.Moment): AttentionItem[] => {
  const items: AttentionItem[] = [];

  events.forEach((event) => {
    const { status, start, end } = event;
    if (status === 'archived') return;

    if (isLiveStatus(status) && end && end.isBefore(now)) {
      items.push({ event, severity: 'urgent', message: `Ended ${end.fromNow()} but is still ${status} — archive it` });
      return;
    }

    if (status === 'pending' && start && (!end || end.isAfter(now))) {
      if (start.isBefore(now)) {
        items.push({ event, severity: 'urgent', message: `Should be live since ${start.fromNow()} but is still ${status}` });
        return;
      }
      if (start.diff(now, 'days', true) <= SOON_DAYS) {
        items.push({ event, severity: 'urgent', message: `Starts ${start.fromNow()} but is still ${status}` });
        return;
      }
    }

    if (status === 'pending') {
      items.push({ event, severity: 'info', message: 'Draft awaiting review' });
    }
  });

  return items.sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'urgent' ? -1 : 1;
    return (a.event.start?.valueOf() ?? Infinity) - (b.event.start?.valueOf() ?? Infinity);
  });
};

/*****************************************************************************
 ** Small building blocks
*****************************************************************************/

const StatusDot: React.FC<{ status: EventStatus }> = ({ status }) => (
  <CircleIcon sx={{ fontSize: 10, color: STATUS_META[status].color, flexShrink: 0 }} />
);

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  px: 1.5,
  py: 1.25,
  borderRadius: '10px',
  cursor: 'pointer',
  transition: 'background 0.15s ease',
  '&:hover': { background: DS.inset },
  '&:hover .row-chevron': { opacity: 1, transform: 'translateX(2px)' },
};

const RowChevron = () => (
  <ChevronRightIcon
    className="row-chevron"
    sx={{ fontSize: 18, color: DS.textFaint, opacity: 0.5, transition: 'all 0.15s ease', flexShrink: 0 }}
  />
);

const EmptyState: React.FC<{ icon: React.ReactNode; title: string; text: string }> = ({ icon, title, text }) => (
  <Box sx={{ textAlign: 'center', py: 4, color: DS.textMuted }}>
    <Box sx={{ mb: 1, color: DS.textFaint }}>{icon}</Box>
    <Typography sx={{ fontWeight: 600, color: DS.text, fontSize: '14px' }}>{title}</Typography>
    <Typography sx={{ fontSize: '13px', mt: 0.5 }}>{text}</Typography>
  </Box>
);

const StatusTile: React.FC<{ status: EventStatus; count: number; total: number; delay: number; onClick: () => void }> = ({ status, count, total, delay, onClick }) => {
  const meta = STATUS_META[status];
  return (
    <Grid item xs={6} sm={4} md={2.4}>
      <Box
        onClick={onClick}
        sx={{
          height: '100%',
          p: { xs: 2, sm: 2.5 },
          background: DS.surface,
          border: `1px solid ${DS.border}`,
          borderRadius: DS.radius,
          cursor: 'pointer',
          transition: 'border-color 0.2s ease, transform 0.2s ease',
          '&:hover': { borderColor: alpha(meta.color, 0.5), transform: 'translateY(-2px)' },
          ...enterSx(delay),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Overline>{meta.label}</Overline>
          <Box sx={{ color: meta.color, display: 'flex', p: 0.75, borderRadius: '10px', background: alpha(meta.color, 0.12) }}>
            {meta.icon}
          </Box>
        </Box>
        <Typography sx={{ fontSize: '32px', fontWeight: 800, lineHeight: 1, color: DS.text, ...numericSx }}>
          {count}
        </Typography>
        <Typography sx={{ fontSize: '12px', color: DS.textMuted, mt: 0.75, mb: 1.5 }}>
          {meta.description}
        </Typography>
        <Meter value={total ? count / total : 0} color={meta.color} height={4} />
      </Box>
    </Grid>
  );
};

/*****************************************************************************
 ** Page
*****************************************************************************/

const AdminDashboard = () => {
  const navigate = useNavigate();
  const context = usePlayerData();

  const {
    pendingEvents = {},
    privateEvents = {},
    inviteEvents = {},
    publicEvents = {},
    archivedEvents = {},
    isAdmin,
    isCreator,
    loading,
  } = context || ({} as any);

  const allEvents = useMemo(() => [
    ...toDashboardEvents(pendingEvents, 'pending'),
    ...toDashboardEvents(privateEvents, 'private'),
    ...toDashboardEvents(inviteEvents, 'invite'),
    ...toDashboardEvents(publicEvents, 'public'),
    ...toDashboardEvents(archivedEvents, 'archived'),
  ], [pendingEvents, privateEvents, inviteEvents, publicEvents, archivedEvents]);

  const now = moment.utc();
  const attentionItems = useMemo(() => getAttentionItems(allEvents, now), [allEvents]); // eslint-disable-line react-hooks/exhaustive-deps

  const liveEvents = allEvents
    .filter((e) => isLiveStatus(e.status) && e.start && e.end && e.start.isSameOrBefore(now) && e.end.isAfter(now))
    .sort((a, b) => a.end!.valueOf() - b.end!.valueOf());

  const upcomingEvents = allEvents
    .filter((e) => e.status !== 'archived' && e.start && e.start.isAfter(now))
    .sort((a, b) => a.start!.valueOf() - b.start!.valueOf())
    .slice(0, MAX_LIST_ITEMS);

  if (!context) {
    return (
      <AdminWrapper>
        <Alert severity="error">Unable to load dashboard data</Alert>
      </AdminWrapper>
    );
  }

  const counts: Record<EventStatus, number> = {
    pending: Object.keys(pendingEvents).length,
    private: Object.keys(privateEvents).length,
    invite: Object.keys(inviteEvents).length,
    public: Object.keys(publicEvents).length,
    archived: Object.keys(archivedEvents).length,
  };
  const totalEvents = counts.pending + counts.private + counts.invite + counts.public + counts.archived;
  const urgentCount = attentionItems.filter((i) => i.severity === 'urgent').length;

  const openEventsTab = (status: EventStatus) => navigate('/admin/events', { state: { tab: STATUS_META[status].tab } });
  const openEvent = (event: DashboardEvent) =>
    event.status === 'pending' ? navigate(`/admin/edit-event/${event.eventID}`) : openEventsTab(event.status);

  const calendarEvents: CalendarEvent[] = allEvents
    .filter((event) => event.status !== 'archived' && event.start && event.end)
    .map((event) => ({
      id: `${event.status}-${event.eventID}`,
      name: event.eventName,
      start: event.start!,
      end: event.end!,
      color: STATUS_META[event.status].color,
      statusLabel: STATUS_META[event.status].label,
      onClick: () => openEvent(event),
    }));

  const quickActions = [
    {
      title: 'Add New Event',
      description: 'Create a new disc golf event',
      icon: <AddIcon fontSize="small" />,
      color: DS.brand,
      onClick: () => navigate('/admin/add-event'),
      disabled: !isAdmin && !isCreator,
    },
    {
      title: 'Manage Events',
      description: 'View and edit existing events',
      icon: <ListIcon fontSize="small" />,
      color: DS.info,
      onClick: () => navigate('/admin/events'),
      disabled: false,
    },
    {
      title: 'Admin Settings',
      description: 'Configure system settings',
      icon: <SettingsIcon fontSize="small" />,
      color: '#8E33FF',
      onClick: () => navigate('/admin/settings'),
      disabled: !isAdmin,
    },
    {
      title: 'Bulk Export',
      description: 'Export event data in bulk',
      icon: <UploadIcon fontSize="small" />,
      color: DS.warning,
      onClick: () => navigate('/admin/events'), // Will add bulk actions later
      disabled: false,
    },
  ];

  if (loading) {
    return (
      <AdminWrapper>
        <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
          <Typography sx={{ color: DS.textMuted }}>Loading dashboard...</Typography>
        </Box>
      </AdminWrapper>
    );
  }

  const headerSubtitle = [
    `${totalEvents} events`,
    `${liveEvents.length} live now`,
    urgentCount > 0 ? `${urgentCount} need attention` : 'nothing urgent',
  ].join(' · ');

  return (
    <AdminWrapper>
      <Grid container spacing={gridSpacing} sx={{ mb: 6 }}>
        <PageHeader
          overline="Admin"
          title="Event dashboard"
          subtitle={headerSubtitle}
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!isAdmin && !isCreator}
              onClick={() => navigate('/admin/add-event')}
              sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, px: 2.5 }}
            >
              New event
            </Button>
          }
        />

        {!isAdmin && !isCreator && (
          <Grid item xs={12}>
            <Alert severity="warning">
              You have limited admin permissions. Some features may be restricted.
            </Alert>
          </Grid>
        )}

        {/* Event overview */}
        {(Object.keys(STATUS_META) as EventStatus[]).map((status, index) => (
          <StatusTile
            key={status}
            status={status}
            count={counts[status]}
            total={totalEvents}
            delay={0.05 * (index + 1)}
            onClick={() => openEventsTab(status)}
          />
        ))}

        {/* Needs attention */}
        <Panel
          title="Needs attention"
          subtitle="Pending events that should be live, finished events to archive, and drafts to review"
          grid={{ xs: 12, md: 7 }}
          delay={0.25}
          action={
            urgentCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: '2px', borderRadius: '999px', background: alpha(DS.loss, 0.14), color: DS.loss, fontSize: '12px', fontWeight: 700 }}>
                {urgentCount} urgent
              </Box>
            )
          }
        >
          {attentionItems.length === 0 ? (
            <EmptyState icon={<CheckIcon />} title="All clear" text="Every event matches its schedule." />
          ) : (
            <Box sx={{ mx: -1.5 }}>
              {attentionItems.slice(0, MAX_LIST_ITEMS).map(({ event, message, severity }) => (
                <Box key={`${event.status}-${event.eventID}`} sx={rowSx} onClick={() => openEvent(event)}>
                  {severity === 'urgent'
                    ? <WarningIcon sx={{ fontSize: 18, color: DS.loss, flexShrink: 0 }} />
                    : <ScheduleIcon sx={{ fontSize: 18, color: DS.warning, flexShrink: 0 }} />}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontSize: '14px', fontWeight: 600, color: DS.text }}>
                      {event.eventName}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: '12px', color: DS.textMuted }}>
                      {message}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: STATUS_META[event.status].color, flexShrink: 0 }}>
                    {STATUS_META[event.status].label}
                  </Typography>
                  <RowChevron />
                </Box>
              ))}
              {attentionItems.length > MAX_LIST_ITEMS && (
                <Button size="small" onClick={() => navigate('/admin/events')} sx={{ ml: 1, mt: 1, textTransform: 'none', color: DS.textMuted }}>
                  +{attentionItems.length - MAX_LIST_ITEMS} more — view all events
                </Button>
              )}
            </Box>
          )}
        </Panel>

        {/* Live & upcoming */}
        <Panel title="Live now" subtitle="Public and invite-only events currently running" grid={{ xs: 12, md: 5 }} delay={0.3}>
          {liveEvents.length === 0 ? (
            <EmptyState icon={<PublicIcon />} title="No live events" text="Nothing is running right now." />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {liveEvents.map((event) => {
                const duration = event.end!.diff(event.start!);
                const progress = duration > 0 ? now.diff(event.start!) / duration : 0;
                return (
                  <Box key={event.eventID} onClick={() => openEvent(event)} sx={{ cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: '14px', fontWeight: 600, color: DS.text }}>
                          {event.eventName}
                        </Typography>
                        {event.status === 'private' && (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0, px: 0.75, borderRadius: '999px', background: alpha(DS.info, 0.14), color: DS.info, fontSize: '11px', fontWeight: 700, lineHeight: '18px' }}>
                            <KeyIcon sx={{ fontSize: 11 }} /> Code only
                          </Box>
                        )}
                        {event.status === 'invite' && (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0, px: 0.75, borderRadius: '999px', background: alpha(DS.violet, 0.14), color: DS.violet, fontSize: '11px', fontWeight: 700, lineHeight: '18px' }}>
                            <MailIcon sx={{ fontSize: 11 }} /> Invite only
                          </Box>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '12px', color: DS.textMuted, flexShrink: 0, ...numericSx }}>
                        ends {event.end!.fromNow()}
                      </Typography>
                    </Box>
                    <Meter value={progress} color={STATUS_META[event.status].color} height={6} />
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <Overline>Up next</Overline>
            {upcomingEvents.length === 0 ? (
              <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 1 }}>No scheduled events.</Typography>
            ) : (
              <Box sx={{ mx: -1.5, mt: 0.5 }}>
                {upcomingEvents.map((event) => (
                  <Box key={`${event.status}-${event.eventID}`} sx={{ ...rowSx, py: 1 }} onClick={() => openEvent(event)}>
                    <StatusDot status={event.status} />
                    <Typography noWrap sx={{ fontSize: '13px', fontWeight: 600, color: DS.text, flex: 1, minWidth: 0 }}>
                      {event.eventName}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: DS.textMuted, flexShrink: 0, ...numericSx }}>
                      {event.start!.format('D MMM HH:mm')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Panel>

        {/* Quick actions */}
        <Panel title="Quick actions" delay={0.35}>
          <Grid container spacing={1.5}>
            {quickActions.map(({ title, description, icon, color, onClick, disabled }) => (
              <Grid item xs={12} sm={6} md={3} key={title}>
                <Box
                  onClick={!disabled ? onClick : undefined}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: '12px',
                    border: `1px solid ${DS.border}`,
                    background: DS.inset,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.45 : 1,
                    transition: 'border-color 0.2s ease, background 0.2s ease',
                    ...(!disabled && {
                      '&:hover': { borderColor: alpha(color, 0.5), background: alpha(color, 0.06) },
                    }),
                  }}
                >
                  <Box sx={{ color, display: 'flex', p: 1, borderRadius: '10px', background: alpha(color, 0.14), flexShrink: 0 }}>
                    {disabled ? <LockIcon fontSize="small" /> : icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: DS.text }}>{title}</Typography>
                    <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>{description}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Panel>

        <Panel title="Event schedule" subtitle="All times in UTC · select a day to see its events, click an event to open it" delay={0.4}>
          <EventCalendar
            events={calendarEvents}
            legend={(Object.keys(STATUS_META) as EventStatus[]).filter((status) => status !== 'archived').map((status) => ({ label: STATUS_META[status].label, color: STATUS_META[status].color }))}
          />
        </Panel>
      </Grid>
    </AdminWrapper>
  );
};

export default AdminDashboard;
